/* global gapi */
import React, { useState, useEffect, useRef } from 'react';

import * as Sentry from '@sentry/browser';

import GoogleAnalyticsAuth from '@hydra/common/components/GoogleAnalytics/GoogleAnalyticsAuth';
import { useGoogleAnalyticsErrorContext } from '@hydra/common/context/googleAnalyticsError';
import Translate from '@hydra/common/components/i18n';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import { LocalStorage } from 'ttl-localstorage';
LocalStorage.synchronous = true;

const useStyles = makeStyles({
  loader: {
    textAlign: 'center'
  }
});

async function loadChart (query) {
  return new Promise((resolve, reject) => {
    const report = new gapi.analytics.report.Data({ query });
    report.on('error', response => {
      if (!response.error) {
        return Sentry.captureMessage(response);
      }
      const error = new Error(response.error.message);
      error.code = response.error.code;
      reject(error);
    });
    report.on('success', resolve);
    report.execute();
  });
}

function GoogleAnalyticsDataWrapper ({
  report,
  viewId,
  Component,
  loadingComponent
}) {
  const classes = useStyles();
  const [response, setResponse] = useState(null);
  const loadingRef = useRef(false);
  const { gaError, setGaError } = useGoogleAnalyticsErrorContext();

  useEffect(() => {
    const query = { ...report.query, ids: 'ga:' + viewId };
    async function loadData () {
      loadingRef.current = true;
      try {
        const response = await loadChart(query);
        LocalStorage.put(key, response, 3600 * 3);
        setResponse(response);
      } catch (error) {
        setGaError(error.code);
      }
    }
    const key = JSON.stringify(query);
    const cachedContent = LocalStorage.get(key);

    if (cachedContent) {
      setResponse(cachedContent);
    } else if (!loadingRef.current) {
      loadData();
    }
  }, [report, viewId, setGaError]);

  if (gaError) {
    return (
      <div>
        <Translate content='google_analytics_graph_error' component='div' />
      </div>
    );
  }

  if (!response) {
    return (
      <div className={classes.loader}>
        {loadingComponent}
      </div>
    );
  }

  return <Component data={response.rows} />;
}

export function GoogleAnalyticsDataAuthWrapper ({
  accessToken,
  report,
  viewId,
  Component,
  loadingComponent = (<CircularProgress />)
}) {
  if (!accessToken) {
    return (
      <div>
        <Translate content='google_analytics_graph_error' component='div' />
      </div>
    );
  }

  return (
    <GoogleAnalyticsAuth accessToken={accessToken}>
      <GoogleAnalyticsDataWrapper
        report={report}
        viewId={viewId}
        Component={Component}
        loadingComponent={loadingComponent}
      />
    </GoogleAnalyticsAuth>
  );
}

export default GoogleAnalyticsDataWrapper;
